import type { AuthProvider, AuthSession } from './auth-provider';
import { cognitoAuthProvider } from './cognito-provider';
import { mockAuthProvider } from './mock-provider';
import { clearSession, persistSession } from './token-storage';

// Selection du provider via env : mock par defaut en dev (sans reseau), Cognito reel en prod.
// Override possible via .env.development.local avec VITE_AUTH_MOCK=false pour tester contre Cognito en dev.
const useMock = import.meta.env.VITE_AUTH_MOCK !== 'false';
const provider: AuthProvider = useMock ? mockAuthProvider : cognitoAuthProvider;

// Marge de securite avant expiration : on rafraichit au plus tard 2 min avant l'expiry pour
// absorber le RTT Cognito (~200-500 ms) + horloge utilisateur potentiellement decalee.
// 13 min effectif d'usage sur un access_token Cognito de 15 min -> compromis raisonnable.
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

function createAuthStore() {
  let session = $state<AuthSession | null>(null);
  // initialized = true une fois que restore() a fini (succes ou non). Tant que false, le layout
  // ne doit PAS rediriger vers /home, sinon on a une race : auth pas encore restauree -> redirect
  // -> apres restore -> redirect /home auth'd vers /office, ce qui court-circuite /welcome.
  let initialized = $state(false);
  // Circuit-breaker pendant le logout. Mis a true des le debut de logout(), empeche tout
  // $effect du layout de re-appeler sessionStore.start() pendant la fenetre asynchrone entre
  // la stop() backend et la navigation Cognito /logout (l'access_token reste valide cote
  // Cognito apres le logout cote serveur, donc un start() reflex au mauvais moment relancerait
  // silencieusement une nouvelle session EC2). Reset en cas d'echec de logout pour permettre
  // un retry, et survivra au navigateur via la nav redirect (cf comment dans logout()).
  let loggingOut = $state(false);
  // Single-flight : tous les appels concurrent a getValidAccessToken pendant un refresh
  // partagent la meme Promise. Sans cela, N appels API simultanes declencheraient N refresh
  // qui peuvent se concurrencer cote Cognito (rotation des refresh_tokens en parallele = un
  // seul gagne, les autres invalides et les sessions correspondantes meurent silencieusement).
  let refreshInFlight: Promise<string> | null = null;

  function setSession(next: AuthSession | null) {
    session = next;
    if (next) persistSession(next);
    else clearSession();
  }

  return {
    get session() {
      return session;
    },
    get user() {
      return session?.user ?? null;
    },
    /**
     * Authentifie tant qu'une session existe. L'expiration de l'access_token n'est pas un
     * critere de deconnexion : on peut toujours refresh tant que le refresh_token n'a pas
     * expire (2 h). Le check stricte se fait dans getValidAccessToken qui peut throw.
     */
    get isAuthenticated() {
      return session !== null;
    },
    get initialized() {
      return initialized;
    },
    /** True entre le moment ou logout() est appele et la fin de la navigation Cognito. Les
     *  $effect qui declenchent des side-effects authentifies (sessionStore.start, fetch
     *  backend autre que /stop) doivent skipper quand ce flag est true. */
    get loggingOut() {
      return loggingOut;
    },

    /**
     * Leve le flag loggingOut AVANT le debut de la sequence (avant sessionStore.stop). Necessaire
     * car sessionStore.stop() fait resetState() qui ecrit phase='idle' ; or Svelte 5 trace les
     * $state lus transitivement dans un $effect, donc le $effect du layout qui appelle
     * sessionStore.start() track phase. Le write phase='idle' re-queue le $effect, qui fire
     * AVANT auth.logout() (toujours dans la meme tick async), avec auth.isAuthenticated encore
     * true et loggingOut encore false -> POST /start parasite. En levant le flag en amont, le
     * $effect re-run skip via le guard if (auth.loggingOut) return.
     *
     * fullLogout() est le seul caller legitime ; reset via cancelLogout() si la sequence echoue
     * avant d'atteindre auth.logout().
     */
    beginLogout() {
      loggingOut = true;
    },
    /** Reset du flag si la sequence logout est interrompue (erreur reseau sur /stop, etc.). */
    cancelLogout() {
      loggingOut = false;
    },

    async login(returnTo: string) {
      await provider.login(returnTo);
    },

    async handleCallback() {
      const result = await provider.handleCallback();
      setSession(result.session);
      return result.returnTo;
    },

    async logout() {
      // Lever le flag AVANT provider.logout() : l'access_token reste valide cote Cognito
      // jusqu'a son expiration naturelle (15 min), donc un $effect qui re-call sessionStore.start()
      // pendant la fenetre logout reussirait a creer une nouvelle session EC2 silencieusement.
      // Le layout $effect consulte auth.loggingOut et skip start() tant que ce flag est true.
      loggingOut = true;
      try {
        await provider.logout();
        // Skip setSession(null) si le provider redirige (cognito) : window.location.assign a deja ete
        // appele et le browser navigue. setSession ici declencherait un $effect parasite entre temps
        // (ex: goto /home?returnTo=/welcome visible dans la barre d'URL avant que Cognito navigue).
        // En mock (redirectsOnLogout=false), on doit purger le state pour que le layout redirige.
        if (!provider.redirectsOnLogout) {
          setSession(null);
        }
      } catch (err) {
        // Reset le flag pour permettre un retry (ex: network error sur le redirect Cognito).
        // Sinon le user reste coince en "logout perpetuel" jusqu'au reload.
        loggingOut = false;
        throw err;
      }
    },

    /** Restauration silencieuse au demarrage : relit ce que persistSession a ecrit. */
    async restore() {
      try {
        const restored = await provider.restoreSilent();
        // On affecte directement sans re-persister : la donnee vient deja du sessionStorage.
        if (restored) session = restored;
      } finally {
        initialized = true;
      }
    },

    /**
     * Retourne un access_token valide pour les ~2 prochaines minutes. Refresh transparent si
     * proche de l'expiration. Throw si pas de session ou si le refresh echoue (refresh_token
     * expire ou revoque) -> le caller doit alors rediriger vers le login Cognito.
     *
     * Single-flight : un refresh en cours est partage par tous les appelants concurrent.
     */
    async getValidAccessToken(): Promise<string> {
      if (!session) throw new Error('not_authenticated');
      const expiresIn = session.expiresAt - Date.now();
      if (expiresIn > REFRESH_MARGIN_MS) return session.accessToken;

      if (!refreshInFlight) {
        const refreshToken = session.refreshToken;
        refreshInFlight = provider
          .refresh(refreshToken)
          .then((next) => {
            setSession(next);
            return next.accessToken;
          })
          .catch((err) => {
            // Refresh echoue : la session est morte cote serveur, on purge cote client pour
            // que le prochain $effect du layout redirige vers /home.
            setSession(null);
            throw err;
          })
          .finally(() => {
            refreshInFlight = null;
          });
      }
      return refreshInFlight;
    }
  };
}

export const auth = createAuthStore();
